import React, { useRef } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { useMapsLoader } from './MapsProvider';

/**
 * Google Places Autocomplete wrapped to look identical to the existing
 * checkout text input. When the user picks a suggestion, we parse the
 * `address_components` and call `onPlaceSelected` with the broken-out
 * fields so the parent form can populate `street1`, `city`, `state`,
 * `zip`, and `country` in one go.
 *
 * Falls back to a plain `<input>` (no autocomplete) when the Maps script
 * isn't loaded — preserves the existing UX exactly.
 */

export interface ParsedPlace {
  street1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  formatted: string;
  lat: number | null;
  lng: number | null;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (parsed: ParsedPlace) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  name?: string;
}

function pick(comps: google.maps.GeocoderAddressComponent[] | undefined, type: string): string {
  return comps?.find((c) => c.types.includes(type))?.long_name ?? '';
}

function pickShort(
  comps: google.maps.GeocoderAddressComponent[] | undefined,
  type: string
): string {
  return comps?.find((c) => c.types.includes(type))?.short_name ?? '';
}

export const AddressAutocompleteInput: React.FC<Props> = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  className,
  required,
  name = 'street1',
}) => {
  const { isLoaded, isConfigured } = useMapsLoader();
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handlePlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place) return;
    const comps = place.address_components;

    const streetNumber = pick(comps, 'street_number');
    const route = pick(comps, 'route');
    const street1 = [streetNumber, route].filter(Boolean).join(' ');

    const city =
      pick(comps, 'locality') ||
      pick(comps, 'postal_town') ||
      pick(comps, 'sublocality_level_1') ||
      pick(comps, 'administrative_area_level_2');

    const state = pick(comps, 'administrative_area_level_1');
    const zip = pick(comps, 'postal_code');
    const country = pickShort(comps, 'country');

    const formatted = place.formatted_address ?? value;
    const lat = place.geometry?.location?.lat() ?? null;
    const lng = place.geometry?.location?.lng() ?? null;

    onChange(street1 || formatted);
    onPlaceSelected({ street1, city, state, zip, country, formatted, lat, lng });
  };

  // Plain fallback when Maps isn't ready – behaves identically to the
  // pre-existing checkout input so users never see a broken state.
  if (!isLoaded || !isConfigured) {
    return (
      <input
        className={className}
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    );
  }

  return (
    <Autocomplete
      onLoad={(ac) => {
        acRef.current = ac;
        ac.setFields(['address_components', 'formatted_address', 'geometry']);
      }}
      onPlaceChanged={handlePlaceChanged}
      options={{ types: ['address'], componentRestrictions: { country: ['us', 'ca'] } }}
    >
      <input
        className={className}
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    </Autocomplete>
  );
};
